import React, {useState} from 'react';
import {Flex, AvatarGroup, Avatar, useTheme, Box} from '@chakra-ui/react';
import {TextG10} from '../typography';
import {UsersListModal} from '../UsersList';
import UserAvatar from '../UserAvatar';
import {useFetchEventBookmarkedUsers} from '../../hooks/useEvent';
import {useFetchOfferBookmarkedUsers} from '../../hooks/useOffers';

type ArticleType = 'event' | 'offer' | 'meetup';

interface NeighborsInterestedDisplayProps {
  articleId: number;
  articleType: ArticleType;
  bookmarksCount: number;
  compact?: boolean;
}

/**
 * A standardized component to display "X Neighbors Interested" across all card types.
 * Shows compressed avatar icons and a clickable text that opens a modal with the full list.
 * Used in BookmarkAhead to show bookmark-based interest (not RSVPs).
 */
const NeighborsInterestedDisplay: React.FC<NeighborsInterestedDisplayProps> = ({
  articleId,
  articleType,
  bookmarksCount,
  compact = false,
}) => {
  const theme = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Determine which hook to use based on article type
  const isOffer = articleType === 'offer';
  const isEvent = articleType === 'event' || articleType === 'meetup';

  // Conditionally call hooks based on type
  const {data: offerData} = useFetchOfferBookmarkedUsers(articleId, isOffer && bookmarksCount > 0);
  const {data: eventData} = useFetchEventBookmarkedUsers(articleId, isEvent && bookmarksCount > 0);

  // Get users from the appropriate data source
  const data = isOffer ? offerData : eventData;
  const firstPageUsers = data?.pages[0]?.data?.data || [];
  const totalCount = data?.pages[0]?.data?.total || bookmarksCount;

  // Determine the fetch hook and chat route based on type
  const fetchUsersHook = isOffer ? useFetchOfferBookmarkedUsers : useFetchEventBookmarkedUsers;
  const chatRoute = isEvent ? `/${articleType === 'meetup' ? 'meet-up' : 'event'}/${articleId}/chat` : undefined;

  if (bookmarksCount === 0) return null;

  // Create display users array with real users + placeholders
  const placeholdersNeeded = Math.max(0, Math.min(3, totalCount) - firstPageUsers.length);
  const placeholders = Array(placeholdersNeeded).fill({user_id: null, name: '', handle: ''});
  const displayUsers = [...firstPageUsers.slice(0, 3), ...placeholders].slice(0, 3);

  return (
    <>
      <Flex
        gap={compact ? 1 : 2}
        align="center"
        cursor="pointer"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsModalOpen(true);
        }}
        _hover={{opacity: 0.8}}
        transition="opacity 0.2s">
        {displayUsers.length > 0 && (
          <AvatarGroup size="xs" max={3} spacing={-1}>
            {displayUsers.map((user, index) => (
              user.user_id ? (
                <UserAvatar
                  key={user.user_id}
                  user={{
                    id: user.user_id,
                    name: user.name,
                    surname: user.surname,
                    handle: user.handle,
                    profilePhotoUrl: user.profile_photo_url,
                  } as any}
                  size="xs"
                  avatar_image={user.profile_photo_url || undefined}
                  sx={{
                    border: '1.5px solid white',
                    width: '20px',
                    height: '20px',
                    '& > img': {
                      width: '20px',
                      height: '20px',
                    },
                  }}
                />
              ) : (
                <Avatar
                  key={`placeholder-${index}`}
                  size="xs"
                  bg="#ccc"
                  sx={{
                    border: '1.5px solid white',
                    width: '20px',
                    height: '20px',
                  }}
                />
              )
            ))}
          </AvatarGroup>
        )}
        <TextG10
          fontWeight="500"
          color={theme.colors.lightBlue}
          whiteSpace="nowrap"
          _hover={{textDecoration: 'underline'}}>
          {totalCount} Neighbor{totalCount !== 1 ? 's' : ''} Interested
        </TextG10>
      </Flex>

      <UsersListModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Neighbors Interested"
        totalCount={totalCount}
        fetchUsersHook={fetchUsersHook}
        entityId={articleId}
        chatRoute={chatRoute}
      />
    </>
  );
};

export default NeighborsInterestedDisplay;
